import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import ReactFlow, {
    Background, Controls, MiniMap, Panel,
    useNodesState, useEdgesState, addEdge,
    MarkerType, Handle, Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { Button } from '../components/ui/button';
import {
    ChevronLeft, Save, Play, Pause, Mail, Clock, CheckSquare,
    GitBranch, Sparkles, Tag, BarChart3, Bell, Flag, Plus,
    Layout, Users, Loader2, Trash2, Zap, Globe, Target, Timer,
    FileText, Send, Settings, ShieldCheck, BookTemplate, AlertTriangle, X,
    MessageSquareQuote, MessageCircle, Check, Lightbulb, Copy
} from 'lucide-react';
import StepConfigPanel from '../components/workflow/StepConfigPanel';
import EmailTemplatePanel from '../components/workflow/EmailTemplatePanel';
// FR-013 hotfix v4 (Peleg): wrap inserted template HTML in a text block
// so the visual editor has content to render + edit, instead of opening
// to a blank canvas because email_blocks was null.
import { createBlock } from '../components/email-builder/emailBlockRegistry';
import TriggerPanel from '../components/workflow/TriggerPanel';
import CampaignAnalytics from '../components/workflow/CampaignAnalytics';
import ExecutionLogViewer from '../components/workflow/ExecutionLogViewer';
import NodeExecutionPanel from '../components/workflow/NodeExecutionPanel';
import WorkflowSettings from '../components/workflow/WorkflowSettings';
import VoiceButton from '../components/ui/VoiceButton';
import usePipelineDefinition from '../hooks/usePipelineDefinition';
import { dealStageMeta } from '../lib/dealStages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Step Type Config (expanded) ────────────────────────────────────────────

const STEP_CATEGORIES = [
    { category: 'Engagement', steps: [
        { type: 'email', label: 'Send Email', icon: Mail, color: 'bg-blue-50 border-blue-300 text-blue-700' },
        { type: 'notification', label: 'Notify Team', icon: Bell, color: 'bg-sky-50 border-sky-300 text-sky-700' },
        { type: 'task', label: 'Create Task', icon: CheckSquare, color: 'bg-purple-50 border-purple-300 text-purple-700' },
        { type: 'request_testimonial', label: 'Request Testimonial', icon: MessageSquareQuote, color: 'bg-pink-50 border-pink-300 text-pink-700' },
        { type: 'send_whatsapp', label: 'Send WhatsApp', icon: MessageCircle, color: 'bg-green-50 border-green-300 text-green-700' },
        { type: 'agentic_email', label: 'AI Personalized Email', icon: Sparkles, color: 'bg-violet-50 border-violet-300 text-violet-700' },
    ]},
    { category: 'Timing', steps: [
        { type: 'wait', label: 'Wait', icon: Clock, color: 'bg-amber-50 border-amber-300 text-amber-700' },
        { type: 'wait_for_event', label: 'Wait for Event', icon: Timer, color: 'bg-amber-50 border-amber-400 text-amber-800' },
        { type: 'delay_until', label: 'Delay Until', icon: Timer, color: 'bg-orange-50 border-orange-300 text-orange-700' },
    ]},
    { category: 'Logic', steps: [
        { type: 'if_else', label: 'If/Else', icon: GitBranch, color: 'bg-stone-50 border-stone-400 text-stone-700' },
        { type: 'ab_split', label: 'A/B Split', icon: Zap, color: 'bg-violet-50 border-violet-300 text-violet-700' },
        { type: 'branch', label: 'Branch', icon: GitBranch, color: 'bg-stone-100 border-stone-400 text-stone-600' },
        { type: 'ai_branch', label: 'AI Branch', icon: Sparkles, color: 'bg-amber-50 border-amber-400 text-amber-700' },
    ]},
    { category: 'Actions', steps: [
        { type: 'tag_update', label: 'Update Tag', icon: Tag, color: 'bg-emerald-50 border-emerald-300 text-emerald-700' },
        { type: 'deal_stage_update', label: 'Update Stage', icon: Target, color: 'bg-indigo-50 border-indigo-300 text-indigo-700' },
        { type: 'update_field', label: 'Update Field', icon: FileText, color: 'bg-teal-50 border-teal-300 text-teal-700' },
        { type: 'assign_user', label: 'Assign User', icon: Users, color: 'bg-cyan-50 border-cyan-300 text-cyan-700' },
    ]},
    { category: 'Advanced', steps: [
        { type: 'goal', label: 'Goal', icon: Target, color: 'bg-emerald-50 border-emerald-400 text-emerald-800' },
        { type: 'webhook_call', label: 'Webhook', icon: Globe, color: 'bg-gray-50 border-gray-300 text-gray-700' },
        { type: 'add_to_workflow', label: 'Add to Workflow', icon: Send, color: 'bg-blue-50 border-blue-200 text-blue-600' },
        { type: 'end', label: 'End', icon: Flag, color: 'bg-red-50 border-red-300 text-red-700' },
    ]},
];

const ALL_STEP_TYPES = STEP_CATEGORIES.flatMap(c => c.steps);
const STEP_COLORS = Object.fromEntries(ALL_STEP_TYPES.map(s => [s.type, s]));

// ─── Trigger Labels ─────────────────────────────────────────────────────────

const TRIGGER_LABELS = {
    contact_created: 'Contact Created',
    tag_added: 'Tag Added',
    tag_removed: 'Tag Removed',
    deal_stage_changed: 'Deal Stage Changed',
    form_submitted: 'Form Submitted',
    booking_confirmed: 'Booking Confirmed',
    event_registered: 'Event Registered',
    event_rsvp_confirmed: 'Event RSVP Confirmed',
    event_rsvp_declined: 'Event RSVP Declined',
    event_attended: 'Event Attended',
    event_no_show: 'Event No-show',
    email_opened: 'Email Opened',
    email_clicked: 'Email Clicked',
    email_replied: 'Email Replied',
    webhook_received: 'Webhook Received',
    manual: 'Manual Enrollment',
    testimonial_submitted: 'Testimonial Submitted',
    whatsapp_message_received: 'WhatsApp Message Received',
};

// ─── Custom Nodes ───────────────────────────────────────────────────────────

function TriggerNode({ data, selected }) {
    return (
        <div className={`w-52 rounded-xl border-2 bg-amber-50 border-amber-400 text-amber-800 px-4 py-3 shadow-sm cursor-pointer hover:shadow-md transition-all ${selected ? 'ring-2 ring-stone-900 ring-offset-2' : ''}`}>
            <div className="flex items-center gap-2 mb-0.5">
                <Zap className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs font-semibold">Trigger</span>
            </div>
            <p className="text-[10px] opacity-80">{data.triggerLabel || 'When...'}</p>
            {data.triggerDetail && <p className="text-[10px] opacity-60 truncate">{data.triggerDetail}</p>}
            <Handle type="source" position={Position.Bottom}
                className="!w-3 !h-3 !bg-amber-400 !border-2 !border-amber-600" />
        </div>
    );
}

function CampaignStepNode({ data, selected }) {
    const config = STEP_COLORS[data.step_type] || STEP_COLORS.email;
    const Icon = config?.icon || Mail;
    const isEnd = data.step_type === 'end';
    const currentlyAt = data._currentlyAt || 0;
    const totalPassed = data._totalPassed || 0;
    return (
        <div className={`group w-52 rounded-xl border-2 ${config?.color || 'bg-stone-50 border-stone-200 text-stone-700'} px-4 py-3 shadow-sm cursor-pointer hover:shadow-md transition-all ${selected ? 'ring-2 ring-stone-900 ring-offset-2' : ''} relative`}>
            {/* FR-014 — Duplicate button (hover-revealed, top-left) */}
            {data.onNodeDuplicate && (
                <button onClick={e => { e.stopPropagation(); data.onNodeDuplicate(data.step_id); }}
                    className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-white border border-stone-300 text-stone-500 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Duplicate this step">
                    <Copy className="w-2.5 h-2.5" />
                </button>
            )}
            {/* Contact count badges (FR-015 — always visible so users have a
                click target even when the count is 0; muted styling at 0 keeps
                the canvas calm but the affordance discoverable) */}
            <div className="absolute -top-2 -right-2 flex gap-0.5">
                <button onClick={e => { e.stopPropagation(); data.onNodeBadgeClick?.(data.step_id, 'current'); }}
                    className={`text-white text-[9px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 shadow-sm transition-colors ${
                        currentlyAt > 0 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-stone-300 hover:bg-stone-400'
                    }`}
                    title={`${currentlyAt} contact${currentlyAt !== 1 ? 's' : ''} currently at this step (click to view)`}>
                    {currentlyAt}
                </button>
                <button onClick={e => { e.stopPropagation(); data.onNodeBadgeClick?.(data.step_id, 'passed'); }}
                    className={`text-white text-[9px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 shadow-sm transition-colors ${
                        totalPassed > 0 ? 'bg-stone-400 hover:bg-stone-500' : 'bg-stone-200 text-stone-500 hover:bg-stone-300'
                    }`}
                    title={`${totalPassed} contact${totalPassed !== 1 ? 's' : ''} have passed through this step (click to view)`}>
                    {totalPassed}
                </button>
            </div>
            <Handle type="target" position={Position.Top}
                className="!w-3 !h-3 !bg-stone-400 !border-2 !border-stone-500" />
            <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs font-semibold truncate">{data.label || config?.label || data.step_type}</span>
            </div>
            {data.step_type === 'email' && data.config?.subject && (
                <p className="text-[10px] opacity-70 truncate">{data.config.subject}</p>
            )}
            {data.step_type === 'wait' && (
                <p className="text-[10px] opacity-70">{data.config?.duration_days || 0}d {data.config?.duration_hours || 0}h</p>
            )}
            {data.step_type === 'if_else' && (
                <p className="text-[10px] opacity-70">{data.config?.condition_group?.conditions?.length || 0} conditions</p>
            )}
            {data.step_type === 'ab_split' && (
                <p className="text-[10px] opacity-70">{data.config?.percentage || 50}% / {100 - (data.config?.percentage || 50)}%</p>
            )}
            {data.step_type === 'tag_update' && (
                <p className="text-[10px] opacity-70">{data.config?.action || 'add'}: {data.config?.tag || '...'}</p>
            )}
            {data.step_type === 'wait_for_event' && (
                <p className="text-[10px] opacity-70">Until: {(data.config?.event_type || '...').replace(/_/g, ' ')}</p>
            )}
            {data.step_type === 'delay_until' && (
                <p className="text-[10px] opacity-70">{data.config?.mode === 'field_offset' ? 'Field offset' : 'Fixed date'}</p>
            )}
            {data.step_type === 'goal' && (
                <p className="text-[10px] opacity-70">{data.config?.condition_group?.conditions?.length || 0} conditions</p>
            )}
            {data.step_type === 'end' && (
                <p className="text-[10px] opacity-70 capitalize">{data.config?.outcome || 'completed'}</p>
            )}
            {/* BUG-045 — branch-type steps have TWO outputs; render labeled handles */}
            {(data.step_type === 'if_else' || data.step_type === 'ab_split') ? (
                <>
                    <Handle type="source" position={Position.Bottom} id={data.step_type === 'if_else' ? 'yes' : 'a'}
                        style={{ left: '25%' }}
                        className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-emerald-600" />
                    <Handle type="source" position={Position.Bottom} id={data.step_type === 'if_else' ? 'no' : 'b'}
                        style={{ left: '75%' }}
                        className="!w-3 !h-3 !bg-stone-400 !border-2 !border-stone-500" />
                    <div className="absolute -bottom-4 left-[25%] -translate-x-1/2 text-[8px] font-bold text-emerald-700 uppercase tracking-wide pointer-events-none">
                        {data.step_type === 'if_else' ? 'Yes' : 'A'}
                    </div>
                    <div className="absolute -bottom-4 left-[75%] -translate-x-1/2 text-[8px] font-bold text-stone-500 uppercase tracking-wide pointer-events-none">
                        {data.step_type === 'if_else' ? 'No' : 'B'}
                    </div>
                </>
            ) : !isEnd && (
                <Handle type="source" position={Position.Bottom}
                    className="!w-3 !h-3 !bg-stone-400 !border-2 !border-stone-500" />
            )}
        </div>
    );
}

const nodeTypes = { campaignStep: CampaignStepNode, triggerNode: TriggerNode };

// ─── Layout ─────────────────────────────────────────────────────────────────

function autoLayout(nodes, edges) {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'TB', nodesep: 60, ranksep: 100 });
    nodes.forEach(n => g.setNode(n.id, { width: 220, height: 80 }));
    edges.forEach(e => g.setEdge(e.source, e.target));
    dagre.layout(g);
    return nodes.map(n => {
        const pos = g.node(n.id);
        return { ...n, position: { x: pos.x - 110, y: pos.y - 40 } };
    });
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function CampaignBuilder() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader, user } = useAuth();
    // the trigger node shows a stage by its label; the config stores the key
    const { definition: salesDefinition } = usePipelineDefinition('sales');

    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [triggers, setTriggers] = useState([]);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // Keep a ref to the latest nodes so handleSave always reads current state
    // (React state updates from setNodes are async and may not be flushed yet)
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    // BUG-043 — Unsaved-changes tracking. Watcher pattern with a hydration
    // grace period: any user-driven change to nodes/edges/triggers/settings
    // flips isDirty true; successful save clears it. Manual setIsDirty
    // would mean adding calls in 10+ handlers and missing one is a silent
    // regression; the watcher catches them all. The 100ms grace covers the
    // cascading effects (trigger-node sync, step-metrics merge) that fire
    // after the initial load setNodes/setEdges/setTriggers settle.
    const [isDirty, setIsDirty] = useState(false);
    const isHydrating = useRef(true);
    useEffect(() => {
        if (isHydrating.current) return;
        setIsDirty(true);
    }, [nodes, edges, triggers, campaign?.settings]);
    useEffect(() => {
        if (!loading) {
            const timer = setTimeout(() => { isHydrating.current = false; }, 100);
            return () => clearTimeout(timer);
        }
    }, [loading]);

    // Browser-level unsaved-exit guard (refresh / tab close / external nav).
    // In-app sidebar nav bypasses this — a known limitation with BrowserRouter
    // (useBlocker requires the React Router 7 data-router migration).
    useEffect(() => {
        const handler = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // Confirm-then-navigate helper for in-component navigation buttons
    // (back arrow, execution report). Sidebar links are not covered.
    const confirmNavigate = useCallback((path) => {
        if (isDirty && !window.confirm('You have unsaved changes. Leave anyway? Your changes will be lost.')) return;
        navigate(path);
    }, [isDirty, navigate]);

    // FR-014 — Duplicate a step. Reads via nodesRef so this callback is
    // stable (no `nodes` dep), which keeps the metrics-merge useEffect
    // from re-firing every time the canvas changes. Defined here (not
    // alongside addStep) so it's in scope for that useEffect's deps.
    const duplicateStep = useCallback((stepId) => {
        const source = nodesRef.current.find(n => n.id === stepId);
        if (!source || source.type !== 'campaignStep') return;
        const newStepId = `step_${Date.now()}`;
        const newNode = {
            id: newStepId,
            type: 'campaignStep',
            position: { x: source.position.x + 50, y: source.position.y + 80 },
            data: {
                ...source.data,
                step_id: newStepId,
                label: source.data?.label ? `${source.data.label} (copy)` : '',
                order: nodesRef.current.length,
                _currentlyAt: 0,
                _totalPassed: 0,
            },
        };
        setNodes(nds => [...nds, newNode]);
        setSelectedNodeId(newStepId);
        toast.success(`Duplicated "${source.data?.label || source.data?.step_type || 'step'}"`);
    }, [setNodes]);

    // FR-013 — Insert a template's subject + body into the currently
    // selected email step. Reads via nodesRef so the callback stays
    // stable for EmailTemplatePanel's deps.
    //
    // FR-013 hotfix v4 (Peleg, 2026-04-29):
    //   v3 cleared email_blocks → modal opened in HTML mode (visual
    //     editor was empty). Peleg: "uses html. so visual editor is not
    //     functional. important to have the ability to make adjustments
    //     via visual editor per-email after inserting template."
    //   v4 now wraps the template body in a single text block (+ a
    //     signature block) so the visual editor has content to render +
    //     edit, AND keeps body in sync so HTML mode also works. On save
    //     the modal re-serialises blocks → body, producing a full
    //     deliverable email document (proper DOCTYPE, MSO conditionals,
    //     mobile responsive scaffolding) — strictly an upgrade for
    //     deliverability vs. the raw template snippet body.
    //
    //   Note: this assumes templates are HTML snippets (which they are
    //   when authored inside the CRM). A template that's already a full
    //   HTML email document would result in nested <html><body> tags
    //   on save, which most clients tolerate but isn't ideal. If we ever
    //   support importing complete HTML documents as templates, we'll
    //   need to detect that and skip the wrapping.
    const insertTemplateIntoStep = useCallback((template) => {
        if (!selectedNodeId) { toast.error('Select an email step first'); return; }
        const node = nodesRef.current.find(n => n.id === selectedNodeId);
        if (!node || node.data?.step_type !== 'email') {
            toast.error('Insert templates into an email step (select one first)');
            return;
        }
        const body = template.body || '';
        const blocks = body.trim()
            ? [
                createBlock('text', { content: body }),
                createBlock('signature'),
            ]
            : null;
        setNodes(nds => nds.map(n => {
            if (n.id !== selectedNodeId) return n;
            return {
                ...n,
                data: {
                    ...n.data,
                    config: {
                        ...(n.data.config || {}),
                        subject: template.subject || '',
                        body,
                        // Replace any prior block-editor state with the
                        // template content wrapped in a single text block.
                        // Modal opens in 'blocks' mode by default; this
                        // ensures the visual editor renders the template,
                        // not an empty canvas. Settings reset to defaults.
                        email_blocks: blocks,
                        email_settings: null,
                    },
                },
            };
        }));
        toast.success(`Inserted "${template.name}"`);
    }, [selectedNodeId, setNodes]);

    // FR-018 — Edit connectors. ReactFlow's onEdgeUpdate fires when the
    // user drags an existing edge endpoint to a different node — we
    // rewrite the matching edge's source/target. Without this, edges
    // could only be deleted + redrawn (Peleg's complaint).
    const onEdgeUpdate = useCallback((oldEdge, newConnection) => {
        setEdges(eds => eds.map(e => {
            if (e.id !== oldEdge.id) return e;
            return {
                ...e,
                source: newConnection.source,
                target: newConnection.target,
                sourceHandle: newConnection.sourceHandle,
                targetHandle: newConnection.targetHandle,
            };
        }));
    }, [setEdges]);

    // AI Chat state
    const [showAIChat, setShowAIChat] = useState(false);
    const [showTemplatesPanel, setShowTemplatesPanel] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);

    // Node execution panel state
    const [nodeExecStepId, setNodeExecStepId] = useState(null);
    const [nodeExecMode, setNodeExecMode] = useState('passed');
    const [stepMetrics, setStepMetrics] = useState({});

    // AI Tag Suggestions state
    const [showTagSuggestions, setShowTagSuggestions] = useState(false);
    const [tagSuggestions, setTagSuggestions] = useState([]);
    const [tagSuggestionsLoading, setTagSuggestionsLoading] = useState(false);
    const [acceptedTagIds, setAcceptedTagIds] = useState(new Set());

    // Load campaign
    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${API}/campaigns/${campaignId}`, { headers: getAuthHeader() });
                setCampaign(res.data);
                setTriggers(res.data.triggers || []);
                const rfNodes = (res.data.steps || []).map(s => ({
                    id: s.step_id,
                    type: 'campaignStep',
                    position: { x: s.position_x || 0, y: s.position_y || 0 },
                    data: { ...s },
                }));
                const rfEdges = (res.data.edges || []).map(e => {
                    // BUG-045 — re-derive sourceHandle from the edge label so
                    // branch-type edges re-bind to the right handle on reload.
                    const labelToHandle = { yes: 'yes', no: 'no', a: 'a', b: 'b' };
                    const sourceHandle = labelToHandle[(e.label || '').toLowerCase()] || null;
                    return {
                        id: e.edge_id,
                        source: e.source_step_id,
                        sourceHandle,
                        target: e.target_step_id,
                        label: e.label || '',
                        type: 'smoothstep',
                        style: { stroke: '#a8a29e', strokeWidth: 2 },
                        markerEnd: { type: MarkerType.ArrowClosed, width: 15, height: 15, color: '#a8a29e' },
                        labelStyle: { fontSize: 10, fill: '#78716c' },
                        labelBgStyle: { fill: '#FAFAF5', stroke: '#e7e5e4' },
                        labelBgPadding: [4, 2],
                        data: { condition: e.condition },
                    };
                });
                const hasPositions = rfNodes.some(n => n.position.x !== 0 || n.position.y !== 0);
                setNodes(hasPositions ? rfNodes : autoLayout(rfNodes, rfEdges));
                setEdges(rfEdges);
            } catch { toast.error('Failed to load campaign'); navigate('/campaigns'); }
            finally { setLoading(false); }
        };
        load();
    }, [campaignId, getAuthHeader, navigate, setNodes, setEdges]);

    // Fetch step metrics for node badges (only for active campaigns with enrollments)
    useEffect(() => {
        if (!campaign || !campaignId) return;
        const fetchMetrics = async () => {
            try {
                const res = await axios.get(`${API}/campaigns/${campaignId}/analytics`, { headers: getAuthHeader() });
                const metrics = {};
                (res.data?.step_metrics || []).forEach(m => {
                    metrics[m.step_id] = { currently_at: m.currently_at || 0, total_passed: m.total || 0 };
                });
                setStepMetrics(metrics);
            } catch { /* silent */ }
        };
        fetchMetrics();
        // Refresh every 30s for active campaigns
        const interval = campaign.status === 'active' ? setInterval(fetchMetrics, 30000) : null;
        return () => { if (interval) clearInterval(interval); };
    }, [campaignId, campaign?.status, getAuthHeader]);

    // Merge step metrics into node data + bind onNodeBadgeClick on every
    // campaignStep node. FR-015: removed the `if (empty) return` early
    // exit so the click handler binds even when no metrics have arrived
    // (draft campaigns, fresh load) — otherwise the always-visible badges
    // from CampaignStepNode would be unclickable.
    useEffect(() => {
        setNodes(nds => nds.map(n => {
            if (n.type !== 'campaignStep') return n;
            const m = stepMetrics[n.id] || {};
            return {
                ...n,
                data: {
                    ...n.data,
                    _currentlyAt: m.currently_at || 0,
                    _totalPassed: m.total_passed || 0,
                    onNodeBadgeClick: (stepId, mode) => { setNodeExecStepId(stepId); setNodeExecMode(mode); },
                    onNodeDuplicate: duplicateStep,
                },
            };
        }));
    }, [stepMetrics, setNodes, duplicateStep]);

    // Sync trigger nodes onto the canvas when triggers change
    useEffect(() => {
        setNodes(nds => {
            // Remove existing trigger nodes
            const stepNodes = nds.filter(n => n.type !== 'triggerNode');
            // Build new trigger nodes
            const triggerNodes = triggers.map((t, i) => {
                const triggerId = t.trigger_id || `trigger_${i}`;
                const existingNode = nds.find(n => n.id === triggerId);
                const triggerLabel = TRIGGER_LABELS[t.trigger_type] || t.trigger_type?.replace(/_/g, ' ') || 'Trigger';
                let detail = '';
                if (['event_registered', 'event_attended', 'event_no_show'].includes(t.trigger_type) && t.config?.event_id) {
                    detail = 'Specific event';
                } else if (t.trigger_type === 'form_submitted' && t.config?.form_id) {
                    detail = 'Specific form';
                } else if (t.trigger_type === 'tag_added' && t.config?.tag) {
                    detail = `Tag: ${t.config.tag}`;
                } else if (t.trigger_type === 'deal_stage_changed' && t.config?.to_stage) {
                    detail = `→ ${dealStageMeta(salesDefinition, t.config.to_stage).label}`;
                }
                return {
                    id: triggerId,
                    type: 'triggerNode',
                    position: existingNode?.position || { x: 180 + i * 270, y: 0 },
                    draggable: true,
                    data: { trigger: t, triggerLabel, triggerDetail: detail },
                };
            });
            return [...triggerNodes, ...stepNodes];
        });
    }, [triggers, setNodes, salesDefinition]);

    // Get selected step data
    const selectedStep = useMemo(() => {
        if (!selectedNodeId) return null;
        const node = nodes.find(n => n.id === selectedNodeId);
        if (node?.type === 'triggerNode') return null; // Triggers configured via TriggerPanel
        return node?.data || null;
    }, [selectedNodeId, nodes]);

    // v2.16.39 BUG-055 — place a new node adjacent to the currently-selected
    // node, or to the right of the rightmost existing node. Previously we
    // used a fixed 3×N grid (x = 100 + (order % 3) * 260, y = 100 + …) which
    // overlapped earlier nodes once `order` exceeded the initial fill, and
    // often placed new nodes outside the visible viewport.
    const computeNextPosition = useCallback(() => {
        const selected = selectedNodeId ? nodes.find(n => n.id === selectedNodeId) : null;
        if (selected?.position) {
            // Place just below the selected node (sequences read top-to-bottom).
            return { x: selected.position.x, y: selected.position.y + 160 };
        }
        // Fallback: below the node with the largest y. Keeps the flow linear
        // and within view when panning to the latest addition.
        if (nodes.length) {
            const bottom = nodes.reduce(
                (max, n) => (n.position && n.position.y > max.y ? n.position : max),
                { x: 100, y: 100 },
            );
            return { x: bottom.x, y: bottom.y + 160 };
        }
        return { x: 100, y: 100 };
    }, [selectedNodeId, nodes]);

    // Add step
    const addStep = useCallback((stepType) => {
        const stepId = `step_${Date.now()}`;
        const order = nodes.length;
        const defaultConfig = {
            email: { subject: '', body: '', use_disc: true, use_voice: true },
            wait: { duration_days: 1, duration_hours: 0 },
            end: { outcome: 'completed' },
            if_else: { condition_group: { logic: 'and', conditions: [] } },
            ab_split: { percentage: 50 },
            tag_update: { action: 'add', tag: '' },
            wait_for_event: { event_type: 'email_opened', timeout_hours: 72 },
            delay_until: { mode: 'fixed', target_datetime: '' },
            goal: { condition_group: { logic: 'and', conditions: [] } },
            task: { title: '', description: '', priority: 'medium', assign_to: 'owner' },
            notification: { message: '', notify_user: 'owner' },
            webhook_call: { url: '', method: 'POST', body: '' },
            request_testimonial: { testimonial_request_id: '', email_subject: '', email_body: '' },
            send_whatsapp: { template_name: '', message_body: '' },
        };
        const newNode = {
            id: stepId,
            type: 'campaignStep',
            position: computeNextPosition(),
            data: {
                step_id: stepId,
                step_type: stepType,
                label: STEP_COLORS[stepType]?.label || stepType.replace(/_/g, ' '),
                config: defaultConfig[stepType] || {},
                order,
            },
        };
        setNodes(nds => [...nds, newNode]);
        setSelectedNodeId(stepId);
    }, [nodes, setNodes, computeNextPosition]);

    // Update step data (from config panel)
    const updateStepData = useCallback((updatedData) => {
        setNodes(nds => nds.map(n =>
            n.id === selectedNodeId ? { ...n, data: { ...n.data, ...updatedData } } : n
        ));
    }, [selectedNodeId, setNodes]);

    // Connect
    const onConnect = useCallback((params) => {
        // BUG-045 — capture which branch-handle the edge came from. Store in
        // edge label (already in the model — no schema change) so we can
        // re-bind sourceHandle on load. Users can re-label freely; the
        // sourceHandle ID survives because ReactFlow holds it in state.
        const handleLabels = { yes: 'Yes', no: 'No', a: 'A', b: 'B' };
        const autoLabel = handleLabels[params.sourceHandle] || '';
        setEdges(eds => addEdge({
            ...params,
            id: `edge_${Date.now()}`,
            label: autoLabel,
            type: 'smoothstep',
            style: { stroke: '#a8a29e', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, width: 15, height: 15, color: '#a8a29e' },
            labelStyle: { fontSize: 10, fill: '#78716c' },
            labelBgStyle: { fill: '#FAFAF5', stroke: '#e7e5e4' },
            labelBgPadding: [4, 2],
        }, eds));
    }, [setEdges]);

    const handleAutoLayout = useCallback(() => {
        setNodes(nds => autoLayout(nds, edges));
    }, [edges, setNodes]);

    // AI Tag Suggestions
    const fetchTagSuggestions = async () => {
        setTagSuggestionsLoading(true);
        setAcceptedTagIds(new Set());
        try {
            const stepTypes = nodes.filter(n => n.type !== 'triggerNode').map(n => n.data.step_type);
            const stepLabels = nodes.filter(n => n.type !== 'triggerNode').map(n => n.data.label).filter(Boolean);
            const triggerTypes = triggers.map(t => t.trigger_type).filter(Boolean);

            const res = await axios.post(`${API}/ai/suggest-tags`, {
                campaign_name: campaign?.name || '',
                description: campaign?.description || '',
                step_types: stepTypes,
                step_labels: stepLabels,
                trigger_types: triggerTypes,
            }, { headers: getAuthHeader() });
            setTagSuggestions(res.data.suggestions || []);
        } catch { toast.error('Failed to generate tag suggestions'); }
        finally { setTagSuggestionsLoading(false); }
    };

    const acceptTagSuggestion = (suggestion, idx) => {
        // Add a tag_update step to the campaign
        const stepId = `step_tag_${Date.now()}_${idx}`;
        const order = nodes.length;
        const newNode = {
            id: stepId,
            type: 'campaignStep',
            position: computeNextPosition(),
            data: {
                step_id: stepId,
                step_type: 'tag_update',
                label: `Tag: ${suggestion.tag}`,
                config: { action: suggestion.action || 'add', tag: suggestion.tag },
                order,
            },
        };
        setNodes(nds => [...nds, newNode]);
        setAcceptedTagIds(prev => new Set([...prev, idx]));
        toast.success(`Added tag step: ${suggestion.tag}`);
    };

    const acceptAllTagSuggestions = () => {
        tagSuggestions.forEach((s, idx) => {
            if (!acceptedTagIds.has(idx)) acceptTagSuggestion(s, idx);
        });
    };

    // Save (includes triggers)
    // Uses nodesRef.current to avoid race condition where setNodes() hasn't flushed yet
    const handleSave = async () => {
        setSaving(true);
        try {
            const currentNodes = nodesRef.current;
            const steps = currentNodes.filter(n => n.type !== 'triggerNode').map(n => ({
                step_id: n.id,
                step_type: n.data.step_type,
                label: n.data.label,
                config: n.data.config || {},
                position_x: n.position.x,
                position_y: n.position.y,
                order: n.data.order || 0,
            }));
            // Persist ALL edges including trigger-to-step connections
            const edgeData = edges.map(e => ({
                edge_id: e.id,
                source_step_id: e.source,
                target_step_id: e.target,
                condition: e.data?.condition || null,
                label: e.label || '',
            }));
            await axios.put(`${API}/campaigns/${campaignId}`, {
                name: campaign?.name,
                steps,
                edges: edgeData,
                triggers,
                settings: campaign?.settings || {},
            }, { headers: getAuthHeader() });
            toast.success('Workflow saved');
            setIsDirty(false);
        } catch (err) {
            // BUG-043 — surface server/network detail so users know whether
            // to retry, fix data, or escalate. Old empty catch hid the cause.
            const detail = err?.response?.data?.detail || err?.message || 'Network error';
            toast.error(`Failed to save: ${detail}`, { duration: 8000 });
            console.error('Campaign save failed:', err);
        }
        finally { setSaving(false); }
    };

    const deleteSelected = useCallback(() => {
        if (!selectedNodeId) return;
        // Don't delete trigger nodes from canvas (they're managed via TriggerPanel)
        const node = nodes.find(n => n.id === selectedNodeId);
        if (node?.type === 'triggerNode') return;
        setNodes(nds => nds.filter(n => n.id !== selectedNodeId));
        setEdges(eds => eds.filter(e => e.source !== selectedNodeId && e.target !== selectedNodeId));
        setSelectedNodeId(null);
    }, [selectedNodeId, nodes, setNodes, setEdges]);

    // Check for ai_prompt query param on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const aiPrompt = params.get('ai_prompt');
        if (aiPrompt) {
            setShowAIChat(true);
            // Delay to let state settle before sending
            setTimeout(() => sendAIMessage(aiPrompt), 500);
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sendAIMessage = async (message) => {
        const newMessages = [...chatMessages, { role: 'user', content: message }];
        setChatMessages(newMessages);
        setChatInput('');
        setChatLoading(true);
        try {
            const currentWorkflow = {
                steps: nodes.filter(n => n.type !== 'triggerNode').map(n => ({
                    step_id: n.id, step_type: n.data.step_type, label: n.data.label, config: n.data.config || {},
                })),
                edges: edges.filter(e => !e.source?.startsWith('trigger')).map(e => ({
                    edge_id: e.id, source_step_id: e.source, target_step_id: e.target,
                })),
                triggers,
            };
            const res = await axios.post(`${API}/ai/campaign-builder`, {
                conversation: newMessages, current_workflow: currentWorkflow, prompt: message,
            }, { headers: getAuthHeader() });

            const data = res.data;
            if (data.question) {
                setChatMessages(prev => [...prev, { role: 'assistant', content: data.question }]);
            } else {
                if (data.steps) {
                    const rfNodes = data.steps.map(s => ({
                        id: s.step_id, type: 'campaignStep',
                        position: { x: s.position_x || 250, y: s.position_y || 100 },
                        data: { ...s },
                    }));
                    setNodes(rfNodes);
                }
                if (data.edges) {
                    const rfEdges = data.edges.map(e => ({
                        id: e.edge_id, source: e.source_step_id, target: e.target_step_id,
                        type: 'smoothstep', style: { stroke: '#a8a29e', strokeWidth: 2 },
                        markerEnd: { type: MarkerType.ArrowClosed, width: 15, height: 15, color: '#a8a29e' },
                    }));
                    setEdges(rfEdges);
                }
                if (data.triggers) setTriggers(data.triggers);
                const msg = data.message || 'Workflow updated!';
                setChatMessages(prev => [...prev, { role: 'assistant', content: msg }]);
                toast.success('AI updated the workflow');
            }
        } catch (err) {
            setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
        } finally {
            setChatLoading(false);
        }
    };

    const [validationResults, setValidationResults] = useState(null);

    const handleValidate = async () => {
        try {
            const res = await axios.post(`${API}/campaigns/${campaignId}/validate`, {}, { headers: getAuthHeader() });
            setValidationResults(res.data);
            if (res.data.valid) {
                toast.success('Workflow is valid ✓');
            } else {
                const errCount = (res.data.errors?.length || 0);
                const warnCount = (res.data.warnings?.length || 0);
                toast.warning(`${errCount} error(s), ${warnCount} warning(s) found`);
            }
        } catch { toast.error('Validation failed'); }
    };

    const handleSaveAsTemplate = async () => {
        const templateName = window.prompt('Template name:', campaign?.name || 'My Template');
        if (!templateName) return;
        try {
            await axios.post(`${API}/campaigns/templates/save`, {
                campaign_id: campaignId,
                name: templateName,
            }, { headers: getAuthHeader() });
            toast.success('Saved as template');
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save template'); }
    };

    const toggleStatus = async () => {
        if (!campaign) return;
        const newStatus = campaign.status === 'active' ? 'paused' : 'active';
        try {
            await axios.put(`${API}/campaigns/${campaignId}`, { status: newStatus }, { headers: getAuthHeader() });
            setCampaign(c => ({ ...c, status: newStatus }));
            toast.success(`Workflow ${newStatus}`);
        } catch { toast.error('Failed to update status'); }
    };

    if (loading) {
        return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>;
    }

    return (
        <div className="h-screen flex flex-col bg-stone-50">
            {/* Top Bar */}
            <div className="flex justify-between items-center sticky top-0 z-10 bg-white border-b border-stone-200 px-4 py-3 sm:px-6 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <button onClick={() => confirmNavigate('/campaigns')} className="p-1.5 text-stone-400 hover:text-stone-700">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <input
                            value={campaign?.name || ''}
                            onChange={e => setCampaign(c => ({ ...c, name: e.target.value }))}
                            onBlur={async () => {
                                if (!campaign?.name?.trim()) return;
                                try {
                                    await axios.put(`${API}/campaigns/${campaignId}`, { name: campaign.name }, { headers: getAuthHeader() });
                                } catch { /* saved on next full save */ }
                            }}
                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                            className="text-sm font-semibold text-stone-900 bg-transparent border-b border-transparent hover:border-stone-300 focus:border-stone-900 focus:outline-none transition-colors w-full max-w-xs"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        />
                        <div className="flex items-center gap-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize ${
                                campaign?.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                campaign?.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                                'bg-stone-100 text-stone-600'
                            }`}>{campaign?.status}</span>
                            <span className="text-xs text-stone-400">{nodes.filter(n => n.type !== 'triggerNode').length} steps | {triggers.length} triggers</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => confirmNavigate(`/campaigns/${campaignId}/execution`)}>
                        <BarChart3 className="w-3.5 h-3.5 mr-1" /> Execution Report
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowTemplatesPanel(!showTemplatesPanel)}
                        title="Email templates (Insert / Save as / Manage)">
                        <FileText className="w-3.5 h-3.5 mr-1" /> Templates
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowAIChat(!showAIChat)}>
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> AI Chat
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowTagSuggestions(!showTagSuggestions); if (!showTagSuggestions && tagSuggestions.length === 0) fetchTagSuggestions(); }}>
                        <Tag className="w-3.5 h-3.5 mr-1" /> Suggest Tags
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleValidate} title="Validate workflow">
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Validate
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleSaveAsTemplate} title="Save as template">
                        <BookTemplate className="w-3.5 h-3.5 mr-1" /> Template
                    </Button>
                    <Button variant="outline" size="sm" onClick={toggleStatus}>
                        {campaign?.status === 'active' ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</> :
                         <><Play className="w-3.5 h-3.5 mr-1" /> Activate</>}
                    </Button>
                    {isDirty && (
                        <span className="text-xs text-amber-600 font-medium px-2" title="You have unsaved changes">
                            Unsaved changes
                        </span>
                    )}
                    <Button size="sm" onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        Save{isDirty ? ' *' : ''}
                    </Button>
                </div>
            </div>

            {/* AI Tag Suggestions Panel */}
            {showTagSuggestions && (
                <div className="mx-4 mb-3 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-indigo-600" />
                            <h3 className="text-sm font-semibold text-indigo-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Tag Suggestions</h3>
                        </div>
                        <div className="flex items-center gap-2">
                            {tagSuggestions.length > 0 && tagSuggestions.some((_, i) => !acceptedTagIds.has(i)) && (
                                <Button size="sm" variant="outline" onClick={acceptAllTagSuggestions}
                                    className="text-xs h-7 border-indigo-200 text-indigo-700 hover:bg-indigo-100">
                                    <Check className="w-3 h-3 mr-1" /> Accept All
                                </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={fetchTagSuggestions} disabled={tagSuggestionsLoading}
                                className="text-xs h-7 border-indigo-200 text-indigo-700 hover:bg-indigo-100">
                                {tagSuggestionsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                <span className="ml-1">Regenerate</span>
                            </Button>
                            <button onClick={() => setShowTagSuggestions(false)} className="p-1 text-indigo-400 hover:text-indigo-600 rounded">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {tagSuggestionsLoading ? (
                        <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-5 h-5 text-indigo-400 animate-spin mr-2" />
                            <span className="text-sm text-indigo-600">Analyzing your campaign workflow...</span>
                        </div>
                    ) : tagSuggestions.length === 0 ? (
                        <p className="text-sm text-indigo-500 text-center py-4">No suggestions yet. Add some steps to your campaign, then click Regenerate.</p>
                    ) : (
                        <div className="space-y-2">
                            {tagSuggestions.map((s, idx) => (
                                <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                                    acceptedTagIds.has(idx) ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-indigo-100'
                                }`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-indigo-100 text-indigo-800">
                                                <Tag className="w-3 h-3" /> {s.tag}
                                            </span>
                                            {s.is_existing && (
                                                <span className="text-[10px] text-stone-400">existing tag</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-stone-500 mt-1 flex items-start gap-1">
                                            <Lightbulb className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                                            {s.reasoning}
                                        </p>
                                        <p className="text-[10px] text-indigo-500 mt-0.5">Apply: {s.apply_at}</p>
                                    </div>
                                    <div className="shrink-0">
                                        {acceptedTagIds.has(idx) ? (
                                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                                                <Check className="w-3.5 h-3.5" /> Added
                                            </span>
                                        ) : (
                                            <Button size="sm" variant="outline" onClick={() => acceptTagSuggestion(s, idx)}
                                                className="text-xs h-7 border-indigo-200 text-indigo-700 hover:bg-indigo-100">
                                                <Plus className="w-3 h-3 mr-1" /> Accept
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Trigger Panel + Settings */}
            <TriggerPanel triggers={triggers} onChange={setTriggers} />
            <WorkflowSettings settings={campaign?.settings || {}} onChange={(s) => setCampaign(prev => ({ ...prev, settings: s }))} />

            {/* Three-column layout: Palette | Canvas | Config */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left: Step Palette + Analytics */}
                <div className="w-56 bg-white border-r border-stone-200 overflow-y-auto flex-shrink-0">
                    {/* Step palette */}
                    <div className="py-3 px-2">
                        {STEP_CATEGORIES.map(cat => (
                            <div key={cat.category} className="mb-3">
                                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider px-2 mb-1">{cat.category}</p>
                                <div className="space-y-0.5">
                                    {cat.steps.map(st => {
                                        const Icon = st.icon;
                                        return (
                                            <button key={st.type} onClick={() => addStep(st.type)}
                                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-stone-700 hover:bg-stone-50 transition-colors">
                                                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                                                <span className="truncate">{st.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Analytics + Logs (only show for saved campaigns) */}
                    {campaign?.campaign_id && (
                        <>
                            <CampaignAnalytics campaignId={campaign.campaign_id} />
                            <ExecutionLogViewer campaignId={campaign.campaign_id} />
                        </>
                    )}
                </div>

                {/* Center: Canvas */}
                <div className="flex-1">
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onEdgeUpdate={onEdgeUpdate}
                        edgeUpdaterRadius={20}
                        onNodeClick={(_, node) => setSelectedNodeId(node.id)}
                        onPaneClick={() => setSelectedNodeId(null)}
                        nodeTypes={nodeTypes}
                        fitView
                        fitViewOptions={{ padding: 0.3 }}
                        minZoom={0.3}
                        maxZoom={2}
                        deleteKeyCode="Backspace"
                    >
                        <Background color="#e7e5e4" gap={20} size={1} />
                        <Controls showInteractive={false} />
                        <MiniMap
                            nodeColor={n => {
                                const t = n.data?.step_type;
                                if (t === 'email') return '#3b82f6';
                                if (t === 'wait' || t === 'wait_for_event' || t === 'delay_until') return '#f59e0b';
                                if (t === 'if_else' || t === 'branch' || t === 'ab_split') return '#a8a29e';
                                if (t === 'end') return '#ef4444';
                                if (t === 'goal') return '#10b981';
                                return '#8b5cf6';
                            }}
                            maskColor="rgba(250, 250, 245, 0.7)"
                        />

                        {/* Canvas Toolbar */}
                        <Panel position="top-right" className="flex gap-1.5">
                            <Button variant="outline" size="sm" onClick={handleAutoLayout} className="bg-white shadow-sm">
                                <Layout className="w-3.5 h-3.5 mr-1" /> Auto Layout
                            </Button>
                            {selectedNodeId && (
                                <Button variant="outline" size="sm" onClick={deleteSelected} className="bg-white shadow-sm text-red-500">
                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                </Button>
                            )}
                        </Panel>

                        {/* Validation Results */}
                        {validationResults && (
                            <Panel position="bottom-left" className="max-w-xs">
                                <div className="bg-white rounded-lg shadow-lg border border-stone-200 p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className={`text-xs font-semibold ${validationResults.valid ? 'text-emerald-600' : 'text-amber-600'}`}>
                                            {validationResults.valid ? '✓ Workflow Valid' : '⚠ Issues Found'}
                                        </span>
                                        <button onClick={() => setValidationResults(null)} className="text-stone-400 hover:text-stone-600 text-xs">✕</button>
                                    </div>
                                    {(validationResults.errors || []).map((e, i) => (
                                        <div key={`e${i}`} className="flex items-start gap-1.5 text-[11px] text-red-600">
                                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                            <span>{e}</span>
                                        </div>
                                    ))}
                                    {(validationResults.warnings || []).map((w, i) => (
                                        <div key={`w${i}`} className="flex items-start gap-1.5 text-[11px] text-amber-600">
                                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                            <span>{w}</span>
                                        </div>
                                    ))}
                                </div>
                            </Panel>
                        )}
                    </ReactFlow>
                </div>

                {/* Right: Step Config Panel */}
                {selectedStep && (
                    <StepConfigPanel
                        step={selectedStep}
                        onUpdate={updateStepData}
                        onClose={() => setSelectedNodeId(null)}
                        campaignSettings={campaign?.settings || {}}
                        campaignId={campaignId}
                        getAuthHeader={getAuthHeader}
                    />
                )}

                {/* Node Execution Panel — shows contacts at/through a step */}
                {nodeExecStepId && !selectedStep && (
                    <NodeExecutionPanel
                        stepId={nodeExecStepId}
                        mode={nodeExecMode}
                        campaignId={campaignId}
                        steps={campaign?.steps || []}
                        onClose={() => setNodeExecStepId(null)}
                    />
                )}

                {/* FR-013 — Email Templates Panel */}
                <EmailTemplatePanel
                    open={showTemplatesPanel}
                    onClose={() => setShowTemplatesPanel(false)}
                    onInsert={selectedStep?.step_type === 'email' ? insertTemplateIntoStep : null}
                    canSaveFromCurrent={selectedStep?.step_type === 'email'}
                    currentEmail={selectedStep?.step_type === 'email' ? {
                        subject: selectedStep.config?.subject || '',
                        body: selectedStep.config?.body || '',
                    } : null}
                    currentStepLabel={selectedStep?.label || ''}
                    isAdmin={!!(user?.is_admin)}
                    getAuthHeader={getAuthHeader}
                />

                {/* AI Chat Panel */}
                {showAIChat && (
                    <div className="w-80 bg-white border-l border-stone-200 flex flex-col flex-shrink-0">
                        <div className="px-3 py-2 border-b border-stone-200 flex items-center justify-between">
                            <span className="text-sm font-semibold text-stone-700 flex items-center gap-1.5">
                                <Sparkles className="w-4 h-4 text-amber-500" /> AI Builder
                            </span>
                            <button onClick={() => setShowAIChat(false)} className="text-stone-400 hover:text-stone-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-3">
                            {chatMessages.length === 0 && (
                                <p className="text-xs text-stone-400 text-center mt-4">Describe what you want to build and the AI will create your workflow.</p>
                            )}
                            {chatMessages.map((m, i) => (
                                <div key={i} className={`text-xs leading-relaxed ${m.role === 'user' ? 'bg-stone-100 rounded-lg p-2 text-stone-700' : 'text-stone-600'}`}>
                                    {m.content}
                                </div>
                            ))}
                            {chatLoading && (
                                <div className="text-xs text-amber-600 flex items-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" /> Building workflow...
                                </div>
                            )}
                        </div>
                        <div className="p-3 border-t border-stone-200 flex gap-1.5">
                            <VoiceButton onTranscript={t => setChatInput(prev => prev ? `${prev} ${t}` : t)} />
                            <input
                                value={chatInput}
                                onChange={e => setChatInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && chatInput.trim()) sendAIMessage(chatInput.trim()); }}
                                placeholder="Describe what to build..."
                                className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                            <button
                                onClick={() => chatInput.trim() && sendAIMessage(chatInput.trim())}
                                disabled={chatLoading || !chatInput.trim()}
                                className="px-2 py-1.5 bg-stone-900 text-white rounded-lg text-xs disabled:opacity-40 hover:bg-stone-800 transition-colors"
                            >
                                Send
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
